/**
 * Customer 360 › Overview as board 04 draws it (the owner's CRM brief, 2026-10-18, §4.5):
 *   · middle — "Delivery · <day>": the day's items with steppers, Skip this day, Add a product, Pause, Note to rider. An
 *     edit is a draft until "Apply & push to apps"; then each edit is one api.apply_customer_change for that date (the
 *     Change drawer's command, the same rules: before the cut-off anyone, after it the CRM manager). Below it the month,
 *     one cell per day in its colour (given, skipped, failed, planned); a click loads that day into the card. Then the
 *     standing plan with Change from a date / Change days.
 *   · right — units per month, the account, the glass bottles, the latest activity.
 * Every figure is a read the page already makes (customerReads.ts); a price that is not known is not shown.
 */
import { dayIsLocked, isSettled } from './changeBasket';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useBillingWindow } from '../finance/financeReads';
import { ChevronLeft, ChevronRight, Minus, Plus, X } from 'lucide-react';
import { rpc, supabase } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { cutoffWords, useBusinessDay } from '../../lib/businessDay';
import { Button, Panel, PanelHeader, cx, plainError } from '../../ui/primitives';
import { toast } from '../../ui/toast';
import { dateTime as fmtDateTime } from '../../ui/format';
import { VariantBottle } from '../../ui/VariantBottle';
import { useOpenChange } from './Customer360Overview';
import { CustomerInsightCards } from './CustomerInsight';
import { ArrangementManage } from './CustomerManage';
import { useOpenTab } from './CustomerViews';
import {
  addDays, balanceSourceLine, dayLines, inr, istToday, ledgerCheckPath, monthStart, nextMonth, outstandingSplit, shortDay, unitsOf, useCalendarMonth, useCustomerBalance, useCustomerTimeline,
  useDeliveryFacts, useMonthBill, usePlannedDoors, type CalendarDay,
} from './customerReads';
import type { Customer360 } from './customerDescriptor';

const prevMonth = (m: string) => { const x = new Date(`${m.slice(0, 7)}-01T00:00:00Z`); x.setUTCMonth(x.getUTCMonth() - 1); return x.toISOString().slice(0, 10); };
const longDay = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const monthTitle = (m: string) => new Date(`${m.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

type DayKind = 'given' | 'failed' | 'skipped' | 'planned' | 'none';
/** A day's colour: given (handed over), failed (an order the door did not get), skipped (planned, then taken off),
 *  planned (today on, still to come); a past day with no order is no colour — nothing was due. */
function kindOf(d: CalendarDay, today: string): DayKind {
  const units = unitsOf(dayLines(d));
  if (d.delivered && d.delivered.given > 0) return 'given';
  if (d.delivered?.outcome === 'NOT_DELIVERED') return 'failed';
  if (d.date >= today) return units > 0 ? 'planned' : (d.planned.length > 0 || d.changes.length > 0) ? 'skipped' : 'none';
  if (d.order && d.order.status !== 'CANCELLED' && units > 0) return 'failed';
  return d.changes.length > 0 && d.planned.length > 0 ? 'skipped' : 'none';
}

/* ── Middle: the day's delivery, the month, the standing plan ───────────────────────────────────────────── */

export function CustomerBoardMiddle({ entity }: { entity: Customer360 }) {
  const today = istToday();
  const [day, setDay] = useState(addDays(today, 1));
  const [month, setMonth] = useState(monthStart(addDays(today, 1)));
  const { can } = useSession();
  // 140900 (the owner, 2026-10-22): a customer with plans at two addresses — the day and the month of one address, or all.
  const doors = usePlannedDoors(entity.id);
  const [door, setDoor] = useState<string | null>(null);
  // The owner's ideas 4 and 10 (2026-10-19): why the customer is their colour, and how many days of bill they owe, first.
  if (!can('subscription.view')) return <div className="grid content-start gap-4"><CustomerInsightCards customerId={entity.id} />
    <Panel><p className="px-4 py-3 text-small text-ink-muted">The delivery calendar is shown to whoever may see subscriptions.</p></Panel></div>;
  return (
    <div className="grid content-start gap-4">
      <CustomerInsightCards customerId={entity.id} />
      {doors.length > 1 ? (
        <div role="group" aria-label="Which address" className="flex flex-wrap items-center gap-1 text-small">
          <span className="mr-1 font-medium">Address</span>
          {[{ locationId: null as string | null, label: 'All' }, ...doors.map((d) => ({ locationId: d.locationId as string | null, label: `${d.label ?? d.code}${d.route ? ` · ${d.route}` : ''}` }))].map((d) => (
            <button key={d.locationId ?? 'all'} type="button" aria-pressed={door === d.locationId} onClick={() => setDoor(d.locationId)}
              className={cx('h-7 rounded-full border px-2.5', door === d.locationId ? 'border-brand bg-brand-soft font-medium text-brand' : 'border-line text-ink-muted hover:bg-surface-sunken')}>{d.label}</button>))}
        </div>) : null}
      <DeliveryDayCard entity={entity} day={day} door={door} />
      <MonthGrid entity={entity} month={month} onMonth={setMonth} selected={day} onPick={(d) => { setDay(d); }} door={door} />
      <StandingPlan entity={entity} />
    </div>
  );
}

interface Draft { qty: Record<string, number>; skip: boolean }

function DeliveryDayCard({ entity, day, door = null }: { entity: Customer360; day: string; door?: string | null }) {
  const business = useBusinessDay();
  const qc = useQueryClient();
  const today = istToday();
  const openChange = useOpenChange();
  const cal = useCalendarMonth(entity.id, monthStart(day), true, door);
  const variants = useQuery({ queryKey: ['variant-ids'], staleTime: 600_000, queryFn: async () => {
    const { data, error } = await supabase.from('product_variants').select('id,code,name,size_class,status');
    if (error) throw error; return (data ?? []) as { id: string; code: string; name: string; size_class: string | null; status: string }[];
  } });
  const d = cal.data?.days.find((x) => x.date === day);
  const lines = d ? dayLines(d) : [];
  const [draft, setDraft] = useState<{ day: string; v: Draft } | null>(null);
  const v = draft?.day === day ? draft.v : null;
  const qtyOf = (code: string, base: number) => (v ? (v.qty[code] ?? base) : base);
  const changed = v ? (v.skip ? 1 : lines.filter((l) => (v.qty[l.variant] ?? l.quantity) !== l.quantity).length) : 0;
  const locked = dayIsLocked(d, day, today, business.data);
  const set = (code: string, n: number) => setDraft({ day, v: { qty: { ...(v?.qty ?? {}), [code]: Math.max(0, Math.min(20, n)) }, skip: v?.skip ?? false } });
  const byCode = new Map((variants.data ?? []).map((x) => [x.code, x]));

  const apply = useMutation({
    mutationFn: async () => {
      const base = { customerId: entity.id, from: day, until: day, reasonCode: 'CUSTOMER_REQUEST', remark: "Changed on the customer's page", ...(door ? { scopeDoorId: door } : {}) };
      const changes: Record<string, unknown>[] = v?.skip ? [{ ...base, kind: 'SKIP' }]
        : lines.filter((l) => (v?.qty[l.variant] ?? l.quantity) !== l.quantity).map((l) => {
            const id = byCode.get(l.variant)?.id; const n = v!.qty[l.variant]!;
            return n === 0 ? { ...base, kind: 'REMOVE', variantId: id } : { ...base, kind: 'QUANTITY', lines: [{ variantId: id, quantity: n }] };
          });
      const done: string[] = [];
      for (const c of changes) {
        const r = await rpc<{ detail?: string }>('apply_customer_change', { p_change: c, p_idempotency_key: crypto.randomUUID() });
        done.push(r.detail ?? 'Applied');
      }
      return done;
    },
    onSuccess: (done) => {
      toast(done.join(' '), { tone: 'ok', ms: 7000 }); setDraft(null);
      for (const k of ['customer-calendar', 'customer-changes', 'customer-delivery-facts']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });

  const state = v?.skip ? { word: 'Skipped · not applied', tone: 'bg-queued-ground text-queued' }
    : changed ? { word: 'Edited · not applied', tone: 'bg-queued-ground text-queued' }
    : d?.delivered && isSettled(d.delivered) ? { word: d.delivered.outcome === 'NOT_DELIVERED' ? 'Not delivered' : 'Delivered', tone: 'bg-accepted-ground text-accepted' }
    : d?.order ? { word: `Order ${d.order.status.toLowerCase()}`, tone: 'bg-accepted-ground text-accepted' }
    : d && d.changes.length ? { word: 'Changed for this day', tone: 'bg-info-ground text-info' }
    : { word: 'Planned · same as standing plan', tone: 'bg-info-ground text-info' };
  const total = v?.skip ? 0 : lines.reduce((n, l) => n + qtyOf(l.variant, l.quantity), 0);

  return (
    <section className="rounded-[var(--radius-panel)] border border-brand bg-surface">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div>
          <h2 className="text-section font-semibold text-ink">Delivery · {longDay(day)}{day === addDays(today, 1) ? ' · tomorrow' : day === today ? ' · today' : ''}</h2>
          <p className="text-small text-ink-muted">Pick a day on the calendar below, edit, then apply. Until {cutoffWords(business.data)} a change goes straight; the round and the plant follow.</p>
        </div>
        <span className={cx('rounded-[var(--radius-badge)] px-1.5 py-0.5 text-badge uppercase', state.tone)}>{state.word}</span>
      </div>
      {!d ? <p className="px-4 py-3 text-small text-ink-muted">{cal.isLoading ? 'Loading the day…' : 'Nothing is known for this day.'}</p> : (
        <>
          {lines.length === 0 ? <p className="px-4 py-3 text-small text-ink-muted">Nothing is planned for this day.</p> : (
            <ul className="divide-y divide-surface-sunken">
              {lines.map((l) => {
                const meta = byCode.get(l.variant); const n = v?.skip ? 0 : qtyOf(l.variant, l.quantity);
                return (
                  <li key={l.variant} className={cx('flex flex-wrap items-center gap-3 px-4 py-3', (v?.skip || n === 0) && 'opacity-60')}>
                    <VariantBottle code={l.variant} size={22} />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-ink">{meta?.name ?? l.variant}</div>
                      <div className="font-mono text-small text-ink-muted">{l.variant}{meta?.size_class ? ` · ${meta.size_class}` : ''}{n !== l.quantity ? ` · was ${l.quantity}` : ''}</div>
                    </div>
                    <div className="inline-flex h-9 items-center rounded-[var(--radius-control)] border border-line">
                      <button type="button" aria-label={`One less ${l.variant}`} disabled={locked || !!v?.skip || n === 0} onClick={() => set(l.variant, n - 1)} className="flex h-full w-9 items-center justify-center text-brand disabled:text-ink-faint"><Minus size={14} /></button>
                      <span className="w-8 text-center font-mono font-semibold tabular-nums">{n}</span>
                      <button type="button" aria-label={`One more ${l.variant}`} disabled={locked || !!v?.skip} onClick={() => set(l.variant, n + 1)} className="flex h-full w-9 items-center justify-center text-brand disabled:text-ink-faint"><Plus size={14} /></button>
                    </div>
                    <button type="button" aria-label={`None of ${l.variant} this day`} disabled={locked || !!v?.skip} onClick={() => set(l.variant, 0)}
                      className="flex size-8 items-center justify-center rounded-[var(--radius-control)] border border-line text-ink-muted hover:bg-surface-sunken disabled:opacity-50"><X size={13} /></button>
                  </li>);
              })}
            </ul>)}
          <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3">
            <Button size="dense" disabled={locked} onClick={() => openChange('add')}><Plus size={13} aria-hidden /> Add a product</Button>
            <Button size="dense" disabled={locked || lines.length === 0} onClick={() => setDraft({ day, v: { qty: v?.qty ?? {}, skip: !v?.skip } })}>{v?.skip ? 'Undo skip' : 'Skip this day'}</Button>
            <Button size="dense" disabled={locked} onClick={() => openChange('pause')}>Pause until…</Button>
            <Button size="dense" disabled={locked} onClick={() => openChange('note')}>Note to rider</Button>
            <span className="ml-auto text-body text-ink-muted">That day: <b className="text-ink">{total} unit{total === 1 ? '' : 's'}</b></span>
          </div>
          {locked && day > today ? <p className="border-t border-line px-4 py-2 text-small text-ink-muted">The cut-off for this day has passed, so it cannot be changed here. A late change is made in the Exception center.</p> : null}
        </>)}
      {changed > 0 ? (
        <div role="region" aria-label="Changes not applied" className="fixed bottom-5 left-1/2 z-30 flex w-[min(60rem,calc(100%-2rem))] -translate-x-1/2 flex-wrap items-center gap-3 rounded-[var(--radius-panel)] bg-brand px-4 py-3 text-on-brand shadow-[var(--shadow-overlay)]">
          <b>{changed} change{changed === 1 ? '' : 's'} not yet in {longDay(day)}'s order</b>
          <span className="text-small opacity-90">Applying writes the change, rebuilds the order and updates the Plant and Delivery apps.</span>
          {apply.error ? <span role="alert" className="w-full text-small">{plainError((apply.error as Error).message)}</span> : null}
          <span className="ml-auto flex gap-2">
            <button type="button" onClick={() => setDraft(null)} className="h-9 rounded-[var(--radius-control)] border border-on-brand/60 px-3 text-body">Discard</button>
            <button type="button" disabled={apply.isPending} onClick={() => apply.mutate()} className="h-9 rounded-[var(--radius-control)] bg-surface px-3 text-body font-medium text-brand">Apply &amp; push to apps</button>
          </span>
        </div>) : null}
    </section>
  );
}

const CELL: Record<DayKind, string> = {
  given: 'bg-brand text-on-brand border-brand',
  failed: 'bg-crit-mark text-on-brand border-crit-mark',
  skipped: 'bg-line text-ink-muted border-line',
  planned: 'bg-brand-soft text-brand border-dashed border-brand/60',
  none: 'bg-surface text-ink-faint border-line',
};

function MonthGrid({ entity, month, onMonth, selected, onPick, door = null }: { entity: Customer360; month: string; onMonth: (m: string) => void; selected: string; onPick: (d: string) => void; door?: string | null }) {
  const today = istToday();
  const cal = useCalendarMonth(entity.id, month, true, door);
  const days = cal.data?.days ?? [];
  const lead = (new Date(`${month}T00:00:00Z`).getUTCDay() + 6) % 7;   // Monday first
  const sum = useMemo(() => {
    const k = days.map((d) => [d, kindOf(d, today)] as const);
    const given = k.filter(([, x]) => x === 'given'); const skipped = k.filter(([, x]) => x === 'skipped'); const planned = k.filter(([, x]) => x === 'planned');
    return { givenDays: given.length, givenUnits: given.reduce((n, [d]) => n + (d.delivered?.given ?? 0), 0), skipped: skipped.map(([d]) => Number(d.date.slice(8))),
             plannedDays: planned.length, plannedUnits: planned.reduce((n, [d]) => n + unitsOf(dayLines(d)), 0) };
  }, [days, today]);
  return (
    <Panel>
      <PanelHeader title={monthTitle(month)} note="Tap a day to change it · units shown in each cell"
        actions={<div className="flex gap-1">
          <Button size="dense" aria-label="Month before" onClick={() => onMonth(prevMonth(month))}><ChevronLeft size={14} /></Button>
          <Button size="dense" aria-label="Month after" onClick={() => onMonth(nextMonth(month))}><ChevronRight size={14} /></Button></div>} />
      <div className="px-4 pb-4">
        <div className="mb-2 flex flex-wrap gap-3 text-small text-ink-muted">
          {(['given', 'skipped', 'failed', 'planned'] as const).map((k) => <span key={k} className="inline-flex items-center gap-1.5"><span aria-hidden className={cx('size-2.5 rounded-full border', CELL[k])} />{k}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5" role="grid" aria-label={monthTitle(month)}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((w) => <div key={w} className="pb-1 text-label uppercase text-ink-muted">{w}</div>)}
          {Array.from({ length: lead }).map((_, i) => <div key={`e${i}`} />)}
          {days.map((d) => {
            const k = kindOf(d, today); const u = d.delivered && isSettled(d.delivered) ? d.delivered.given : unitsOf(dayLines(d));
            return (
              <button key={d.date} type="button" onClick={() => onPick(d.date)} aria-pressed={selected === d.date}
                aria-label={`${longDay(d.date)}: ${k}${u ? `, ${u} units` : ''}`}
                className={cx('flex h-11 flex-col items-start justify-between rounded-[var(--radius-control)] border px-1.5 py-1 text-left', CELL[k], selected === d.date && 'ring-2 ring-ink ring-offset-1')}>
                <span className="text-small font-medium leading-none">{Number(d.date.slice(8))}</span>
                <span className="text-[0.6875rem] leading-none">{k === 'skipped' ? 'skip' : u ? `${u} u` : ''}</span>
              </button>);
          })}
        </div>
        <p className="mt-3 text-small text-ink-muted">
          Given {sum.givenDays} days · {sum.givenUnits} units{sum.skipped.length ? ` · skipped ${sum.skipped.length} (${sum.skipped.join(', ')})` : ''} · planned {sum.plannedDays} days · {sum.plannedUnits} units
        </p>
      </div>
    </Panel>
  );
}

function StandingPlan({ entity }: { entity: Customer360 }) {
  const openTab = useOpenTab();
  const subs = (entity.subscriptions ?? []).filter((s) => s.status !== 'CANCELLED');
  const taken = entity.products.filter((p) => p.takes || p.assigned);
  const first = subs.length ? subs.map((s) => s.start_date).sort()[0] : null;
  return (
    <Panel>
      <PanelHeader title="Standing plan" note={subs.length ? `${subs.map((s) => s.schedule_pattern.toLowerCase()).join(', ')}${first ? ` · since ${shortDay(first)}` : ''}` : 'no standing plan'}
        actions={<Button size="dense" onClick={() => openTab('calendar', 'history')}>History</Button>} />
      <div className="grid gap-3 px-4 pb-4 sm:grid-cols-2">
        <div>
          <div className="text-label uppercase text-ink-muted">Items</div>
          {taken.length === 0 ? <p className="mt-1 text-small text-ink-muted">No item yet.</p> : (
            <ul className="mt-1 grid gap-1 text-body">
              {taken.map((p) => <li key={p.variant_code} className="flex items-center gap-2"><VariantBottle code={p.variant_code} size={18} />{p.variant_name} <span className="font-mono text-small text-ink-muted">{p.variant_code}</span></li>)}
            </ul>)}
        </div>
        <div>
          <div className="text-label uppercase text-ink-muted">Agreements</div>
          <ul className="mt-1 grid gap-1 text-body">
            {subs.map((s) => <li key={s.id}><span className="font-mono">{s.code}</span> · {s.units_per_delivery}/day · <span className="text-ink-muted">{s.status.toLowerCase()}{s.paused_from ? ` · paused from ${shortDay(s.paused_from)}` : ''}</span></li>)}
            {subs.length === 0 ? <li className="text-small text-ink-muted">None</li> : null}
          </ul>
        </div>
      </div>
      {entity.subscriptions !== null ? <div className="border-t border-line px-4 py-3"><ArrangementManage entity={entity} /></div> : null}
    </Panel>
  );
}

/* ── Right: units per month, account, glass bottles, recent ─────────────────────────────────────────────── */

export function CustomerBoardRight({ entity }: { entity: Customer360 }) {
  const openTab = useOpenTab();
  const { can } = useSession();
  const today = istToday();
  const balance = useCustomerBalance(entity.id);
  const bill = useMonthBill(entity.id, today.slice(0, 7));
  const facts = useDeliveryFacts(entity.id);
  const timeline = useCustomerTimeline(entity.id);
  // The months since billing started (read from Finance), up to six.
  const startMonth = useBillingWindow().startMonth;
  const months: string[] = [];
  if (startMonth) for (let m = `${today.slice(0, 7)}-01`; m.slice(0, 7) >= startMonth && months.length < 6; m = prevMonth(m)) months.unshift(m);
  const units = useQuery({ queryKey: ['customer-units-by-month', entity.id, months.join()], enabled: can('customer.payment.view'),
    queryFn: async () => Promise.all(months.map(async (m) => ({ m, n: (await rpc<{ byVariant: { units: number }[] }>('customer_month_bill', { p_customer_id: entity.id, p_month: m })).byVariant.reduce((s, x) => s + x.units, 0) }))) });
  const max = Math.max(1, ...(units.data ?? []).map((x) => x.n));
  const f = facts.data;
  return (
    <>
      {can('customer.payment.view') ? (
        <Panel>
          <PanelHeader title="Units per month" note={months.length > 1 ? `${monthTitle(months[0]!).split(' ')[0]} → ${monthTitle(months[months.length - 1]!).split(' ')[0]} (so far)` : 'billing started in October 2026'} />
          <div className="flex h-36 items-end gap-3 px-4 pb-3" role="img" aria-label={(units.data ?? []).map((x) => `${monthTitle(x.m)}: ${x.n} units`).join('; ')}>
            {(units.data ?? []).map((x, i, all) => (
              <div key={x.m} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-small font-medium tabular-nums">{x.n}</span>
                <div className={cx('w-full max-w-10 rounded-t-[3px]', i === all.length - 1 ? 'bg-series-3' : 'bg-brand')} style={{ height: `${Math.max(4, (x.n / max) * 88)}px` }} />
                <span className="text-small text-ink-muted">{monthTitle(x.m).slice(0, 3)}</span>
              </div>))}
            {!units.data ? <span className="text-small text-ink-muted">…</span> : null}
          </div>
        </Panel>) : null}

      <Panel>
        <PanelHeader title="Account" />
        {balance.error ? <p className="px-4 pb-3 text-small text-ink-muted">Money is shown to whoever may see it.</p> : (
          <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-2 px-4 pb-3 text-body">
            <dt className="text-ink-muted">Outstanding</dt><dd className={cx('text-right font-semibold tabular-nums', (balance.data?.balance_paise ?? 0) > 0 && 'text-refused')}>{inr(balance.data?.balance_paise)}</dd>
            {balance.data ? <dd className="col-span-2 -mt-1.5 text-small text-ink-muted">{outstandingSplit(balance.data)} · {balanceSourceLine(balance.data.source)} · <Link className="text-info underline" to={ledgerCheckPath(entity.code)}>Open in Finance</Link></dd> : null}
            {bill.data ? <><dt className="text-ink-muted">{monthTitle(today).split(' ')[0]} so far</dt><dd className="text-right tabular-nums">{inr(bill.data.charged)}</dd></> : null}
            <dt className="text-ink-muted">Last payment</dt><dd className="text-right tabular-nums">{balance.data?.last_paid_on ? shortDay(balance.data.last_paid_on) : 'never'}</dd>
            <dt className="text-ink-muted">Paid all time</dt><dd className="text-right tabular-nums">{inr(balance.data?.paid_paise)}</dd>
          </dl>)}
        <div className="flex gap-2 border-t border-line px-4 py-3">
          <Button variant="primary" size="dense" onClick={() => openTab('money', 'payments')}>Record payment</Button>
          <Button size="dense" onClick={() => openTab('money', 'statement')}>Send bill</Button>
        </div>
      </Panel>

      {f ? (
        <Panel>
          <PanelHeader title="Glass bottles" />
          <div className="grid grid-cols-2 gap-2 px-4 pb-3 text-center">
            <div><div className="text-2xl font-semibold tabular-nums">{f.bottlesOwed ?? 0}</div><div className="text-small text-ink-muted">held</div></div>
            <div><div className={cx('text-2xl font-semibold tabular-nums', (f.oldestOwedDays ?? 0) >= 7 && 'text-queued')}>{f.bottlesOwed ? `${f.oldestOwedDays ?? 0} d` : '—'}</div><div className="text-small text-ink-muted">oldest out</div></div>
          </div>
          <div className="border-t border-line px-4 py-2 text-small"><button type="button" className="text-brand hover:underline" onClick={() => openTab('deliveries', 'bottles')}>Bottles by label</button></div>
        </Panel>) : null}

      <Panel>
        <PanelHeader title="Recent" actions={<button type="button" className="text-small text-brand hover:underline" onClick={() => openTab('activity', 'history')}>all activity</button>} />
        {(timeline.data ?? []).length === 0 ? <p className="px-4 pb-3 text-small text-ink-muted">{timeline.isLoading ? 'Loading…' : 'Nothing yet.'}</p> : (
          <ol className="grid grid-cols-1 gap-2.5 px-4 pb-3">
            {(timeline.data ?? []).slice(0, 4).map((e, i) => (
              <li key={i} className="min-w-0"><p className="truncate text-body font-medium">{e.what}</p><p className="truncate text-small text-ink-muted">{e.at ? fmtDateTime(e.at) : 'undated'}{e.detail ? ` · ${e.detail}` : ''}</p></li>))}
          </ol>)}
      </Panel>
    </>
  );
}
